/**
 * Trim + lowercase. Exported so `answersMatch` (applyPersonalization.ts) normalizes a rule's
 * `question` and `values` exactly the way the session's answer map was built.
 */
export function normalizeAnswerToken (value: string): string {
  return value.trim().toLowerCase()
}

/**
 * Normalizes `window.funnelAnswers` (or the equivalent context-state copy) into a lookup keyed
 * by normalized question, each mapped to the normalized set of answers the session gave.
 *
 * The input is unvalidated JSON from upstream, so nothing about it is assumed and this never
 * throws: non-object, array, null, or undefined input resolves to an empty map, and values are
 * normalized defensively even though they should already arrive trimmed and percent-decoded.
 * A bare string value (a single-answer question arriving unwrapped) is tolerated alongside
 * `string[]`. Non-string entries and keys/values blank after trimming are skipped; a key with
 * no usable value gets no entry at all, not a present-but-empty set. Two raw keys that
 * normalize to the same token have their values unioned.
 */
export function deriveFunnelAnswers (raw: unknown): Map<string, Set<string>> {
  const result = new Map<string, Set<string>>()
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return result
  }

  Object.entries(raw as Record<string, unknown>).forEach(([key, rawValue]) => {
    const question = normalizeAnswerToken(key)
    if (!question) {
      return
    }
    const values = Array.isArray(rawValue) ? rawValue : [rawValue]
    const answers = result.get(question) ?? new Set<string>()
    values.forEach((value) => {
      if (typeof value !== 'string') {
        return
      }
      const normalized = normalizeAnswerToken(value)
      if (normalized) {
        answers.add(normalized)
      }
    })
    if (answers.size > 0) {
      result.set(question, answers)
    }
  })

  return result
}
